import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '../../contexts/ThemeContext';
import { usePendingSkills } from '../../hooks/useSkills';
import { SkillPreview } from '../../components/skills/SkillPreview';
import { Spinner } from '../../components/common/Spinner';
import { SkillMeta } from '../../types/skill';
import * as skillService from '../../services/skillService';

type ActionState = Record<string, 'approve' | 'reject'>;

const ReviewCard: React.FC<{
  skill: SkillMeta;
  darkMode: boolean;
  loadingAction: string | undefined;
  onApprove: () => void;
  onReject: () => void;
}> = ({ skill, darkMode, loadingAction, onApprove, onReject }) => {
  const [activeTab, setActiveTab] = useState<'preview' | 'raw'>('preview');
  const [notes, setNotes] = useState('');
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-left w-full"
            >
              <h3 className="font-semibold">{skill.display_name}</h3>
              <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {skill.description}
              </p>
            </button>
            <div className="flex flex-wrap gap-2 mt-2">
              <span className={`text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                ~{skill.estimated_tokens.toLocaleString()} tokens
              </span>
              <span className={`text-xs px-1.5 py-0.5 rounded ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                {skill.tags?.length ?? 0} tags
              </span>
              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                by {skill.imported_by || 'unknown'}
                {skill.imported_at && ` | ${new Date(skill.imported_at).toLocaleDateString()}`}
              </span>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={onReject}
              disabled={!!loadingAction}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                loadingAction === 'reject' ? 'opacity-60' : ''
              } ${darkMode ? 'bg-red-900/40 hover:bg-red-800 text-red-300' : 'bg-red-50 hover:bg-red-100 text-red-600'}`}
            >
              {loadingAction === 'reject' ? 'Rejecting...' : 'Reject'}
            </button>
            <button
              onClick={onApprove}
              disabled={!!loadingAction}
              className={`px-3 py-1.5 rounded-md text-sm text-white transition-colors ${
                loadingAction === 'approve' ? 'opacity-60' : ''
              } ${darkMode ? 'bg-green-700 hover:bg-green-600' : 'bg-green-500 hover:bg-green-600'}`}
            >
              {loadingAction === 'approve' ? 'Approving...' : 'Approve'}
            </button>
          </div>
        </div>
      </div>

      {/* Expandable content */}
      {expanded && (
        <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {/* Tabs */}
          <div className={`flex border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            {(['preview', 'raw'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-xs font-medium transition-colors ${
                  activeTab === tab
                    ? darkMode ? 'border-b-2 border-blue-400 text-blue-400' : 'border-b-2 border-blue-500 text-blue-600'
                    : darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'preview' ? 'Preview' : 'Raw Markdown'}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="p-4 max-h-64 overflow-y-auto">
            {activeTab === 'preview' ? (
              <SkillPreview content={skill.description} />
            ) : (
              <pre className={`text-xs whitespace-pre-wrap font-mono ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {`---\nname: ${skill.name}\ndescription: ${skill.description}\ntags: [${(skill.tags || []).join(', ')}]\n---`}
              </pre>
            )}
          </div>

          {/* Admin notes */}
          <div className={`px-4 pb-4`}>
            <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Admin Notes (local only)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes for your reference..."
              className={`mt-1 w-full px-3 py-1.5 rounded-md border text-xs ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400'
              } focus:outline-none focus:ring-1 focus:ring-blue-500`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const SkillReviewQueue: React.FC = () => {
  const { darkMode } = useTheme();
  const queryClient = useQueryClient();
  const { data: pending = [], isLoading } = usePendingSkills();
  const [loadingActions, setLoadingActions] = useState<ActionState>({});

  const handleAction = async (name: string, action: 'approve' | 'reject') => {
    setLoadingActions((prev) => ({ ...prev, [name]: action }));
    try {
      if (action === 'approve') {
        await skillService.approveSkill(name);
      } else {
        await skillService.rejectSkill(name);
      }
      queryClient.invalidateQueries(['skillsPending']);
      queryClient.invalidateQueries(['skills']);
    } finally {
      setLoadingActions((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  return (
    <div className={`container mx-auto p-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Review Queue</h1>
        <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          User-submitted skills awaiting approval ({pending.length} pending)
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-12"><Spinner size="lg" /></div>
      ) : pending.length === 0 ? (
        <div className={`text-center py-16 rounded-lg border border-dashed ${darkMode ? 'border-gray-700 text-gray-400' : 'border-gray-300 text-gray-500'}`}>
          <p className="text-sm">No pending submissions</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pending.map((skill) => (
            <ReviewCard
              key={skill.name}
              skill={skill}
              darkMode={darkMode}
              loadingAction={loadingActions[skill.name]}
              onApprove={() => handleAction(skill.name, 'approve')}
              onReject={() => handleAction(skill.name, 'reject')}
            />
          ))}
        </div>
      )}
    </div>
  );
};
